import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Check, ClipboardList, UserPlus, Wrench, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS, type ProblemStatus } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  DetailRow,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  StatusPill,
  Textarea,
  problemStatusTone,
  severityTone,
} from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useCan } from '../../lib/auth';
import { formatDate, formatDateTime, humanise, relativeTime } from '../../lib/format';

interface ProblemDetail {
  problem: {
    id: string;
    tagNo: string;
    title: string;
    description: string;
    severity: string;
    status: string;
    category: string | null;
    detectedAt: string;
    dueDate: string | null;
    closedAt: string | null;
    rootCauseCategory: string | null;
    rootCauseDetail: string | null;
    correctiveAction: string | null;
    preventiveAction: string | null;
    safetyImpact: boolean;
    qualityImpact: boolean;
    productionImpact: boolean;
    downtimeMinutes: number | null;
    assignedDept: string | null;
    assignedTo: string | null;
    oplId: string | null;
    machineId: string;
    runId: string | null;
    rejectionReason: string | null;
  };
  machineCode: string;
  machineName: string;
  lineName: string;
  componentName: string | null;
  detectedByName: string;
  assigneeName: string | null;
  actions: Array<{
    id: string;
    actionType: string;
    fromStatus: string | null;
    toStatus: string | null;
    notes: string | null;
    hoursSpent: number | null;
    performedAt: string;
    performedByName: string;
  }>;
  evidence: Array<{ id: string; mediaId: string; role: string; caption: string | null }>;
  verifications: Array<{
    id: string;
    outcome: string;
    notes: string | null;
    verifiedAt: string;
    verifiedByName: string;
  }>;
  source: {
    runId: string;
    itemSeq: number | null;
    itemTask: string | null;
    itemStandard: string | null;
    answerNumber: number | null;
    answerText: string | null;
    unit: string | null;
    templateName: string;
    templateFrequency: string;
  } | null;
  allowedTransitions: ProblemStatus[];
}

export function ProblemDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'assign' | 'work' | 'verify' | 'close' | 'opl' | null>(null);

  const canAssign = useCan(PERMISSIONS.PROBLEM_ASSIGN);
  const canAct = useCan(PERMISSIONS.PROBLEM_ACT);
  const canVerify = useCan(PERMISSIONS.PROBLEM_VERIFY);
  const canClose = useCan(PERMISSIONS.PROBLEM_CLOSE);
  const canCreateOpl = useCan(PERMISSIONS.OPL_CREATE);

  const { data, isLoading } = useQuery({
    queryKey: ['problem', id],
    queryFn: () => api.get<ProblemDetail>(`/api/problem-tags/${id}`),
  });

  const act = useMutation({
    mutationFn: async (input: { path: string; body: unknown }) =>
      api.post(`/api/problem-tags/${id}${input.path}`, input.body),
    onSuccess: () => {
      setError(null);
      setDialog(null);
      void queryClient.invalidateQueries();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'That action could not be completed.'),
  });

  const createOpl = useMutation({
    mutationFn: () => api.post<{ opl: { id: string } }>(`/api/opls/from-problem/${id}`, {}),
    onSuccess: (result) => navigate(`/opl/${result.opl.id}`),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not create the OPL.'),
  });

  const { data: categories } = useQuery({
    queryKey: ['problem-categories'],
    queryFn: () =>
      api.get<{
        problemCategories: Array<{ code: string; name: string }>;
        rootCauseCategories: Array<{ code: string; name: string }>;
      }>('/api/problem-tags/categories'),
  });

  if (isLoading || !data) return <Spinner label="Loading problem tag" />;

  const { problem } = data;
  const isClosed = problem.status === 'CLOSED' || problem.status === 'CANCELLED';
  const canRequestVerification = data.allowedTransitions.includes('PENDING_VERIFICATION');
  const awaitingVerification = problem.status === 'PENDING_VERIFICATION';

  return (
    <div>
      <PageHeader
        title={`${problem.tagNo} — ${problem.title}`}
        back={{ to: '/problems', label: 'All problem tags' }}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Link to={`/machines/${problem.machineId}`} className="font-semibold hover:underline">
              {data.machineCode}
            </Link>
            <span>
              {data.componentName ?? 'Machine level'} · {data.lineName}
            </span>
            <StatusPill tone={severityTone(problem.severity)} label={humanise(problem.severity)} />
            <StatusPill tone={problemStatusTone(problem.status)} label={humanise(problem.status)} />
          </span>
        }
        actions={
          <>
            {canAssign && !isClosed ? (
              <Button variant="secondary" icon={UserPlus} onClick={() => setDialog('assign')}>
                Assign
              </Button>
            ) : null}
            {canAct && !isClosed ? (
              <Button variant="secondary" icon={Wrench} onClick={() => setDialog('work')}>
                Record work
              </Button>
            ) : null}
            {canAct && canRequestVerification ? (
              <Button
                variant="secondary"
                onClick={() =>
                  act.mutate({
                    path: '/status',
                    body: { status: 'PENDING_VERIFICATION', notes: 'Ready for verification.' },
                  })
                }
              >
                Ready for verification
              </Button>
            ) : null}
            {canVerify && awaitingVerification ? (
              <Button variant="secondary" icon={Check} onClick={() => setDialog('verify')}>
                Verify
              </Button>
            ) : null}
            {canClose && data.allowedTransitions.includes('CLOSED') ? (
              <Button variant="success" icon={Check} onClick={() => setDialog('close')}>
                Close
              </Button>
            ) : null}
            {canCreateOpl && problem.status === 'CLOSED' && !problem.oplId ? (
              <Button icon={BookOpen} loading={createOpl.isPending} onClick={() => createOpl.mutate()}>
                Create OPL
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid gap-4 p-4 lg:grid-cols-3 lg:p-6">
        <div className="space-y-4 lg:col-span-2">
          {error ? <Callout tone="alarm">{error}</Callout> : null}

          {problem.safetyImpact || problem.severity === 'CRITICAL' ? (
            <Callout tone="alarm" title="Safety-critical">
              This tag must be verified by someone other than whoever carried out the repair
              before it can be closed.
            </Callout>
          ) : null}

          {problem.oplId ? (
            <Callout tone="ok" title="A lesson came out of this">
              <Link to={`/opl/${problem.oplId}`} className="font-bold underline">
                Open the One-Point Lesson →
              </Link>
            </Callout>
          ) : problem.status === 'CLOSED' ? (
            // Stated rather than left blank: "no related OPL" is a finding on a
            // closed problem, not an absence of information.
            <Callout tone="info" title="No related OPL">
              Nothing was captured as a One-Point Lesson from this problem.
            </Callout>
          ) : null}

          <Card>
            <CardHeader title="What was found" />
            <div className="px-4 py-3">
              <p className="text-sm whitespace-pre-line text-steel-800">{problem.description}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {problem.safetyImpact ? <Badge tone="alarm">Safety impact</Badge> : null}
                {problem.qualityImpact ? <Badge tone="warn">Quality impact</Badge> : null}
                {problem.productionImpact ? <Badge tone="warn">Production impact</Badge> : null}
                {problem.downtimeMinutes ? (
                  <Badge tone="warn">{problem.downtimeMinutes} min downtime</Badge>
                ) : null}
              </div>
            </div>
          </Card>

          {data.source ? (
            <Card>
              <CardHeader
                title="Where it came from"
                action={
                  <Link
                    to={`/checklists/${data.source.runId}/review`}
                    className="text-xs font-bold text-info-700 hover:underline"
                  >
                    Open checklist →
                  </Link>
                }
              />
              <div className="px-4 py-3 text-sm">
                <p className="text-steel-600">
                  Detected during{' '}
                  <span className="font-semibold text-steel-900">
                    {humanise(data.source.templateFrequency)} CILT
                  </span>{' '}
                  · {data.source.templateName}
                </p>
                <p className="mt-1 font-semibold text-steel-900">
                  <ClipboardList aria-hidden className="mr-1.5 inline size-4" />
                  Task #{data.source.itemSeq} — {data.source.itemTask}
                </p>
                {data.source.itemStandard ? (
                  <p className="mt-1 text-steel-600">Standard: {data.source.itemStandard}</p>
                ) : null}
                {data.source.answerNumber != null ? (
                  <p className="mt-1 font-bold text-alarm-700">
                    Recorded: {data.source.answerNumber}
                    {data.source.unit ? ` ${data.source.unit}` : ''}
                  </p>
                ) : data.source.answerText ? (
                  <p className="mt-1 font-bold text-alarm-700">
                    Recorded: {data.source.answerText}
                  </p>
                ) : null}
              </div>
            </Card>
          ) : null}

          {data.evidence.length > 0 ? (
            <Card>
              <CardHeader title="Evidence" />
              <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
                {data.evidence.map((item) => (
                  <figure key={item.id}>
                    <img
                      src={`/api/media/${item.mediaId}`}
                      alt={item.caption ?? `${humanise(item.role)} photo`}
                      className="aspect-square w-full rounded-lg border border-steel-200 object-cover"
                      loading="lazy"
                    />
                    <figcaption className="mt-1 text-xs text-steel-500">
                      {humanise(item.role)}
                      {item.caption ? ` — ${item.caption}` : ''}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </Card>
          ) : null}

          <Card>
            <CardHeader title="History" subtitle="Every action taken on this tag" />
            <ol className="px-4 py-2">
              {data.actions.map((action) => (
                <li key={action.id} className="border-b border-steel-100 py-3 last:border-0">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm font-bold text-steel-900">
                      {humanise(action.actionType)}
                      {action.toStatus && action.toStatus !== action.fromStatus
                        ? ` → ${humanise(action.toStatus)}`
                        : ''}
                    </p>
                    <p className="text-xs text-steel-500">
                      {action.performedByName} · {formatDateTime(action.performedAt)}
                    </p>
                  </div>
                  {action.notes ? (
                    <p className="mt-1 text-sm whitespace-pre-line text-steel-700">{action.notes}</p>
                  ) : null}
                  {action.hoursSpent ? (
                    <p className="mt-1 text-xs text-steel-500">{action.hoursSpent} h spent</p>
                  ) : null}
                </li>
              ))}
            </ol>
          </Card>

          {data.verifications.length > 0 ? (
            <Card>
              <CardHeader title="Verifications" />
              <ul className="px-4 py-2">
                {data.verifications.map((verification) => (
                  <li key={verification.id} className="border-b border-steel-100 py-3 last:border-0">
                    <div className="flex items-center justify-between gap-2">
                      <StatusPill
                        tone={verification.outcome === 'ACCEPTED' ? 'ok' : 'alarm'}
                        label={humanise(verification.outcome)}
                      />
                      <p className="text-xs text-steel-500">
                        {verification.verifiedByName} · {formatDateTime(verification.verifiedAt)}
                      </p>
                    </div>
                    {verification.notes ? (
                      <p className="mt-1 text-sm text-steel-700">{verification.notes}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Details" />
            <dl className="px-4 py-2">
              <DetailRow label="Raised by">
                {data.detectedByName} · {relativeTime(problem.detectedAt)}
              </DetailRow>
              <DetailRow label="Assigned to">
                {data.assigneeName ?? problem.assignedDept ?? 'Not assigned'}
              </DetailRow>
              <DetailRow label="Target close date">{formatDate(problem.dueDate)}</DetailRow>
              <DetailRow label="Category">{humanise(problem.category)}</DetailRow>
              <DetailRow label="Root cause">
                {problem.rootCauseCategory ? (
                  <>
                    <span className="font-semibold">{humanise(problem.rootCauseCategory)}</span>
                    {problem.rootCauseDetail ? (
                      <span className="block text-steel-600">{problem.rootCauseDetail}</span>
                    ) : null}
                  </>
                ) : (
                  'Not determined yet'
                )}
              </DetailRow>
              <DetailRow label="Corrective action">{problem.correctiveAction}</DetailRow>
              <DetailRow label="Preventive action">{problem.preventiveAction}</DetailRow>
              {problem.closedAt ? (
                <DetailRow label="Closed">{formatDateTime(problem.closedAt)}</DetailRow>
              ) : null}
              {problem.rejectionReason ? (
                <DetailRow label="Rejected because">{problem.rejectionReason}</DetailRow>
              ) : null}
            </dl>
          </Card>
        </div>
      </div>

      {dialog === 'assign' ? (
        <AssignDialog
          busy={act.isPending}
          onClose={() => setDialog(null)}
          onSubmit={(body) => act.mutate({ path: '/assign', body })}
        />
      ) : null}

      {dialog === 'work' ? (
        <WorkDialog
          rootCauses={categories?.rootCauseCategories ?? []}
          busy={act.isPending}
          onClose={() => setDialog(null)}
          onSubmit={(body) => act.mutate({ path: '/actions', body })}
        />
      ) : null}

      {dialog === 'verify' ? (
        <VerifyDialog
          busy={act.isPending}
          onClose={() => setDialog(null)}
          onSubmit={(body) => act.mutate({ path: '/verify', body })}
        />
      ) : null}

      {dialog === 'close' ? (
        <CloseDialog
          hasCorrectiveAction={Boolean(problem.correctiveAction)}
          busy={act.isPending}
          onClose={() => setDialog(null)}
          onSubmit={(body) => act.mutate({ path: '/close', body })}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

function AssignDialog({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: { assignedTo: string | null; assignedDept: string | null; notes: string }) => void;
}) {
  const [assignedTo, setAssignedTo] = useState('');
  const [dept, setDept] = useState('Maintenance');
  const [notes, setNotes] = useState('');

  const { data } = useQuery({
    queryKey: ['users', 'directory'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; fullName: string; roles: string[] }> }>(
        '/api/users/directory',
      ),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Assign this problem"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            loading={busy}
            disabled={!assignedTo && !dept}
            onClick={() =>
              onSubmit({ assignedTo: assignedTo || null, assignedDept: dept || null, notes })
            }
          >
            Assign
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Person" hint="They get a notification straight away.">
          <Select value={assignedTo} onChange={(event) => setAssignedTo(event.target.value)}>
            <option value="">No specific person</option>
            {(data?.data ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.fullName} ({person.roles.map((r) => humanise(r)).join(', ')})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Department">
          <Input value={dept} onChange={(event) => setDept(event.target.value)} />
        </Field>
        <Field label="Notes">
          <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
        </Field>
      </div>
    </Modal>
  );
}

function WorkDialog({
  rootCauses,
  busy,
  onClose,
  onSubmit,
}: {
  rootCauses: Array<{ code: string; name: string }>;
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [actionType, setActionType] = useState<'DIAGNOSIS' | 'CORRECTIVE' | 'PREVENTIVE' | 'COMMENT'>(
    'DIAGNOSIS',
  );
  const [notes, setNotes] = useState('');
  const [hours, setHours] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [rootCauseDetail, setRootCauseDetail] = useState('');
  const [corrective, setCorrective] = useState('');
  const [preventive, setPreventive] = useState('');

  return (
    <Modal
      open
      onClose={onClose}
      title="Record work"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            loading={busy}
            disabled={notes.trim().length < 3}
            onClick={() =>
              onSubmit({
                actionType,
                notes: notes.trim(),
                hoursSpent: hours ? Number(hours) : null,
                rootCauseCategory: rootCause || undefined,
                rootCauseDetail: rootCauseDetail || undefined,
                correctiveAction: corrective || undefined,
                preventiveAction: preventive || undefined,
              })
            }
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="What are you recording?">
          <Select value={actionType} onChange={(event) => setActionType(event.target.value as never)}>
            <option value="DIAGNOSIS">Diagnosis — what I found</option>
            <option value="CORRECTIVE">Corrective action — what I did</option>
            <option value="PREVENTIVE">Preventive action — stopping a repeat</option>
            <option value="COMMENT">Comment</option>
          </Select>
        </Field>

        <Field label="Notes" required>
          <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={4} />
        </Field>

        {actionType === 'DIAGNOSIS' ? (
          <>
            <Field
              label="Root cause category"
              hint="Structured categories help spot patterns. This does not replace a formal RCA."
            >
              <Select value={rootCause} onChange={(event) => setRootCause(event.target.value)}>
                <option value="">Not determined</option>
                {rootCauses.map((cause) => (
                  <option key={cause.code} value={cause.code}>
                    {cause.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Root cause detail">
              <Textarea
                value={rootCauseDetail}
                onChange={(event) => setRootCauseDetail(event.target.value)}
                rows={2}
              />
            </Field>
          </>
        ) : null}

        {actionType === 'CORRECTIVE' ? (
          <Field label="Corrective action" hint="Required before the tag can be closed.">
            <Textarea
              value={corrective}
              onChange={(event) => setCorrective(event.target.value)}
              rows={2}
            />
          </Field>
        ) : null}

        {actionType === 'PREVENTIVE' ? (
          <Field label="Preventive action">
            <Textarea
              value={preventive}
              onChange={(event) => setPreventive(event.target.value)}
              rows={2}
            />
          </Field>
        ) : null}

        <Field label="Hours spent" hint="Optional">
          <Input
            inputMode="decimal"
            value={hours}
            onChange={(event) => setHours(event.target.value)}
            placeholder="0.5"
          />
        </Field>
      </div>
    </Modal>
  );
}

function VerifyDialog({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: { outcome: string; notes: string }) => void;
}) {
  const [notes, setNotes] = useState('');

  return (
    <Modal
      open
      onClose={onClose}
      title="Verify the fix"
      footer={
        <div className="flex gap-2">
          <Button
            variant="danger"
            fullWidth
            icon={X}
            loading={busy}
            onClick={() => onSubmit({ outcome: 'REJECTED', notes })}
          >
            Not fixed
          </Button>
          <Button
            variant="success"
            fullWidth
            icon={Check}
            loading={busy}
            onClick={() => onSubmit({ outcome: 'ACCEPTED', notes })}
          >
            Confirmed fixed
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Callout tone="info">
          Check the machine yourself before confirming. Rejecting sends the tag back to whoever
          is working on it.
        </Callout>
        <Field label="What did you check?">
          <Textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={4}
            placeholder="e.g. Ran the line for one hour, pressure held at 5.8 bar, no drips."
          />
        </Field>
      </div>
    </Modal>
  );
}

function CloseDialog({
  hasCorrectiveAction,
  busy,
  onClose,
  onSubmit,
}: {
  hasCorrectiveAction: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: { notes: string }) => void;
}) {
  const [notes, setNotes] = useState('');

  return (
    <Modal
      open
      onClose={onClose}
      title="Close this problem tag"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="success"
            fullWidth
            loading={busy}
            disabled={!hasCorrectiveAction}
            onClick={() => onSubmit({ notes })}
          >
            Close tag
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {hasCorrectiveAction ? (
          <Callout tone="info">
            Closing records who closed it and when. Everything stays in the machine history.
          </Callout>
        ) : (
          <Callout tone="warn" title="Corrective action missing">
            Record what was actually done to fix this before closing it.
          </Callout>
        )}
        <Field label="Closing note">
          <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
        </Field>
      </div>
    </Modal>
  );
}
